
import { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const CashBuyerContactForm = ({
  formId,
  title,
  subtitle,
}: {
  formId: string;
  title: string;
  subtitle: string;
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyCondition: '',
    timeline: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('propertyAddress', formData.propertyAddress);
      formBody.append('propertyCondition', formData.propertyCondition);
      formBody.append('timeline', formData.timeline);
      formBody.append('message', formData.message);

      await fetch('https://readdy.ai/api/form/d678mmdbd17lru29kc50', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
          <i className="ri-check-line text-3xl text-teal-600"></i>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
        <p className="text-gray-600">
          We&apos;ve received your information and will contact you within 24
          hours with your no-obligation cash offer.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-100">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600">{subtitle}</p>
      </div>

      <form
        id={formId}
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="Your full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="(915) 555-0123"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="your@email.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Property Address *
          </label>
          <input
            type="text"
            name="propertyAddress"
            required
            value={formData.propertyAddress}
            onChange={(e) =>
              setFormData({ ...formData, propertyAddress: e.target.value })
            }
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            placeholder="123 Main St, El Paso, TX"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Property Condition
            </label>
            <select
              name="propertyCondition"
              value={formData.propertyCondition}
              onChange={(e) =>
                setFormData({ ...formData, propertyCondition: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select condition</option>
              <option value="Excellent">Excellent - Move-in ready</option>
              <option value="Good">Good - Minor repairs needed</option>
              <option value="Fair">Fair - Needs some work</option>
              <option value="Poor">Poor - Major repairs needed</option>
              <option value="Very Poor">Very Poor - Significant issues</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeline to Sell
            </label>
            <select
              name="timeline"
              value={formData.timeline}
              onChange={(e) =>
                setFormData({ ...formData, timeline: e.target.value })
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
            >
              <option value="">Select timeline</option>
              <option value="ASAP">ASAP - Need to sell immediately</option>
              <option value="1-2 weeks">Within 1-2 weeks</option>
              <option value="1 month">Within 1 month</option>
              <option value="1-3 months">Within 1-3 months</option>
              <option value="Just exploring">Just exploring options</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tell Us About Your Situation
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={(e) =>
              setFormData({
                ...formData,
                message: e.target.value.slice(0, 500),
              })
            }
            rows={3}
            maxLength={500}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            placeholder="Any details about your property or situation that would help us..."
          />
          <p className="text-xs text-gray-500 mt-1">
            {formData.message.length}/500 characters
          </p>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin mr-2"></i>
              Submitting...
            </span>
          ) : (
            'Get My Cash Offer Now'
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          Your information is secure and will never be sold. No obligation, no pressure.
        </p>
      </form>
    </div>
  );
};

export default function CashHomeBuyersElPasoPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Companies That Pay Cash for Houses in El Paso | How It Works | TREX RE LLC"
        description="Learn how cash home buyers work in El Paso TX. Discover the process, benefits, and what to expect when selling your house for cash. Get a fair offer in 24 hours with no fees or commissions."
        keywords="cash home buyers El Paso, we buy houses El Paso, sell house for cash El Paso TX, cash for homes El Paso, companies that buy houses for cash, sell my house fast El Paso"
        canonical="/elpaso-tx/cash-home-buyers-el-paso"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Companies That Pay Cash for Houses in El Paso - How It Works',
          description: 'Comprehensive guide to understanding how cash home buying companies work in El Paso, Texas. Learn the process, benefits, and what to expect.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://public.readdy.ai/ai/img_res/a37a6e4a91cc21e63866ed513e73d513.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-500/90 text-white text-sm font-semibold rounded-full mb-6">
                <i className="ri-money-dollar-circle-line mr-2"></i>
                Cash Home Buyers Guide
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Companies That Pay <span className="text-teal-400">Cash for Houses</span> in El Paso
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Discover how cash home buying works, what to expect from the process, and how to get a fair offer for your El Paso property — regardless of its condition.
              </p>

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>No Repairs Needed</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Close in 7-14 Days</span>
                </div>
                <div className="flex items-center text-white">
                  <i className="ri-check-line text-teal-400 mr-2"></i>
                  <span>Zero Fees or Commissions</span>
                </div>
              </div>
            </div>

            <div>
              <CashBuyerContactForm
                formId="hero-cash-buyer-form"
                title="Get Your Cash Offer Today"
                subtitle="No obligation, no pressure — just a fair offer"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-4xl mx-auto px-6 py-16">
        {/* Introduction */}
        <div className="prose prose-lg max-w-none mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you&apos;ve ever searched for ways to sell your house quickly in El Paso, you&apos;ve probably come across advertisements from companies that promise to buy your home for cash. These &quot;we buy houses&quot; companies have become increasingly popular across Texas and the entire country, offering homeowners an alternative to the traditional real estate market. But how exactly do these cash home buying companies work? Are they legitimate? And is selling to a cash buyer the right choice for your situation?
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In this comprehensive guide, we&apos;ll pull back the curtain on the cash home buying industry in El Paso. We&apos;ll explain exactly how the process works from start to finish, discuss the types of companies that buy houses for cash, help you understand what kind of offers to expect, and give you the information you need to make an informed decision about whether this option is right for you.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            At TREX RE LLC, we&apos;ve been buying homes for cash in El Paso and throughout Texas for years. We&apos;ve helped hundreds of homeowners navigate difficult situations — from{' '}
            <a href="/inherited-homes" className="text-teal-600 hover:text-teal-700 font-medium">
              inherited properties
            </a>{' '}
            to homes facing foreclosure, from rental properties with problem tenants to houses that simply need too many repairs. Our goal with this article is to educate you about the entire industry so you can make the best decision for your unique circumstances.
          </p>
        </div>

        {/* What Are Cash Home Buyers Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            What Exactly Are Cash Home Buying Companies?
          </h2>

          <img
            src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20investment%20company%20office%20with%20modern%20interior%20showing%20team%20of%20investors%20reviewing%20property%20documents%20on%20large%20screen%20with%20el%20paso%20texas%20cityscape%20visible%20through%20windows%20warm%20professional%20lighting%20clean%20contemporary%20design&width=800&height=500&seq=cash-buyers-office-002&orientation=landscape"
            alt="Professional cash home buying company office"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Cash home buying companies are real estate investors or investment firms that purchase properties directly from homeowners using their own funds — no bank financing, no mortgage approvals, no waiting for loan underwriting. Because they don&apos;t rely on traditional financing, these companies can close transactions much faster than conventional buyers and can purchase properties in virtually any condition.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The business model is straightforward: cash buyers purchase properties at a discount, then either renovate and resell them (known as &quot;flipping&quot;), rent them out for ongoing income, or hold them as long-term investments. The discount they pay reflects the risk they&apos;re taking, the repairs they&apos;ll need to make, and the holding costs they&apos;ll incur during the renovation and resale process.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            It&apos;s important to understand that not all cash buyers are the same. The industry includes several different types of companies, each with their own approach and business model. Understanding these differences can help you choose the right buyer for your situation.
          </p>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-user-star-line text-teal-600 mr-2"></i>
                Local Real Estate Investors
              </h4>
              <p className="text-gray-700 text-sm">
                These are individuals or small companies based in El Paso who buy properties in their local market. They typically have deep knowledge of El Paso neighborhoods, can make decisions quickly, and often provide more personalized service. Local investors like TREX RE LLC understand the unique aspects of the El Paso market, from the impact of Fort Bliss to the nuances of different neighborhoods.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-building-2-line text-teal-600 mr-2"></i>
                National &quot;We Buy Houses&quot; Franchises
              </h4>
              <p className="text-gray-700 text-sm">
                Large national brands that operate through local franchisees. While they have brand recognition, the actual buyer is usually a local investor operating under the franchise name. Quality and service can vary significantly depending on the individual franchisee.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-computer-line text-teal-600 mr-2"></i>
                iBuyers (Instant Buyers)
              </h4>
              <p className="text-gray-700 text-sm">
                Technology companies like Opendoor and Offerpad that use algorithms to make instant offers on homes. They typically focus on newer homes in good condition and may not buy properties that need significant repairs. Their offers are often closer to market value but come with service fees that can be similar to real estate commissions.
              </p>
            </div>
            <div className="bg-gray-50 p-5 rounded-lg">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-funds-line text-teal-600 mr-2"></i>
                Institutional Investors
              </h4>
              <p className="text-gray-700 text-sm">
                Large investment funds and corporations that buy properties to hold as rental investments. These buyers are typically looking for properties in specific price ranges and conditions that fit their portfolio criteria.
              </p>
            </div>
          </div>
        </section>

        {/* How the Process Works Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How the Cash Home Buying Process Works: Step by Step
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the biggest advantages of selling to a cash buyer is the simplicity of the process. Unlike traditional home sales that can take months and involve dozens of steps, selling to a cash buyer typically follows a streamlined process that can be completed in as little as one to two weeks. Here&apos;s exactly what to expect when you work with a reputable cash home buying company in El Paso.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=step%20by%20step%20process%20infographic%20showing%20home%20selling%20journey%20with%20icons%20representing%20contact%20evaluation%20offer%20closing%20with%20clean%20modern%20design%20warm%20colors%20professional%20business%20illustration%20style&width=800&height=500&seq=cash-buyers-process-003&orientation=landscape"
            alt="Cash home buying process steps"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 1: Initial Contact and Information Gathering
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The process begins when you reach out to a cash buyer — either by phone, through their website, or by responding to their marketing. During this initial conversation, the buyer will ask basic questions about your property: the address, approximate square footage, number of bedrooms and bathrooms, the general condition of the home, and your timeline for selling.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            This information helps the buyer determine if your property fits their buying criteria and allows them to do preliminary research before making an offer. Reputable buyers will also ask about your situation — why you&apos;re selling, what challenges you&apos;re facing, and what your goals are. This isn&apos;t just small talk; understanding your needs helps them structure an offer that works for your specific circumstances.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 2: Property Evaluation
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            After the initial conversation, most cash buyers will want to see the property in person. This isn&apos;t a formal inspection like you&apos;d have with a traditional sale — it&apos;s more of a walkthrough to assess the property&apos;s condition and verify the information you provided. The buyer will look at the overall structure, major systems (roof, HVAC, plumbing, electrical), and any obvious repairs that will be needed.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Some iBuyers and national companies may skip the in-person visit initially, making offers based on photos and public data. However, they typically include inspection contingencies that allow them to adjust their offer after seeing the property. Local investors usually prefer to see the property upfront so they can make a firm offer without contingencies.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 3: Receiving Your Cash Offer
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Based on their evaluation, the cash buyer will present you with an offer. This offer should be in writing and clearly state the purchase price, any conditions or contingencies, the proposed closing date, and who will pay closing costs. Reputable buyers will explain how they arrived at their offer price and answer any questions you have.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At TREX RE LLC, we typically present offers within 24-48 hours of viewing a property. Our offers are straightforward — we tell you exactly what we&apos;ll pay, and that number doesn&apos;t change unless we discover something significantly different from what was disclosed. There are no hidden fees, no commissions, and no last-minute surprises.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 4: Accepting the Offer and Opening Escrow
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If you decide to accept the offer, you&apos;ll sign a purchase agreement. The buyer will then open escrow with a title company, which acts as a neutral third party to handle the transaction. The title company will conduct a title search to ensure there are no liens or encumbrances on the property that would prevent the sale.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If there are{' '}
            <a href="/title-issues" className="text-teal-600 hover:text-teal-700 font-medium">
              title issues
            </a>{' '}
            — such as old liens, boundary disputes, or unclear ownership — experienced cash buyers often have solutions. They may work with title companies to resolve issues, purchase title insurance to cover potential problems, or structure the deal in ways that address these challenges. This is one area where working with an experienced local buyer can be particularly valuable.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Step 5: Closing and Getting Paid
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Once the title work is complete, you&apos;ll schedule a closing date. At closing, you&apos;ll sign the deed transferring ownership to the buyer, and you&apos;ll receive your payment. With cash buyers, this payment comes as a cashier&apos;s check or wire transfer — there&apos;s no waiting for bank financing to come through because the buyer already has the funds available.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The entire process, from initial contact to closing, typically takes between 7 and 21 days, depending on the complexity of the title work and your preferred timeline. Compare this to traditional sales, which average 45-60 days even when everything goes smoothly — and can take much longer if there are financing issues, inspection problems, or buyer contingencies.
          </p>

          <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-3">
              <i className="ri-time-line mr-2 text-teal-600"></i>
              Timeline Comparison
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="font-semibold text-gray-900 mb-2">Cash Sale</p>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Initial contact: Day 1</li>
                  <li>• Property visit: Days 1-3</li>
                  <li>• Receive offer: Days 2-4</li>
                  <li>• Title work: Days 5-14</li>
                  <li>• Closing: Days 7-21</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-gray-900 mb-2">Traditional Sale</p>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Prep and list: Weeks 1-4</li>
                  <li>• Showings: Weeks 2-12+</li>
                  <li>• Receive offer: Variable</li>
                  <li>• Inspections/financing: Weeks 4-8</li>
                  <li>• Closing: Days 45-90+</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why Homeowners Choose Cash Buyers Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Why El Paso Homeowners Choose to Sell for Cash
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling a home for cash isn&apos;t the right choice for everyone, but for many homeowners in El Paso, it&apos;s the perfect solution to their specific situation. Understanding the common reasons people choose cash buyers can help you determine if this option makes sense for you.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=relieved%20happy%20homeowner%20family%20standing%20outside%20their%20home%20in%20el%20paso%20texas%20with%20sold%20sign%20feeling%20stress%20free%20and%20satisfied%20after%20successful%20quick%20home%20sale%20warm%20sunset%20lighting%20showing%20emotional%20relief%20and%20new%20beginnings&width=800&height=500&seq=cash-buyers-relief-004&orientation=landscape"
            alt="Happy homeowners after selling their house for cash"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Speed and Certainty
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            The most common reason homeowners choose cash buyers is the need for speed. Life doesn&apos;t always give us the luxury of waiting months for a traditional sale. Job relocations, especially common in El Paso with the military community at Fort Bliss, often come with tight timelines. Divorce situations may require quick property division. Financial difficulties might mean you need to sell before{' '}
            <a href="/avoid-foreclosure" className="text-teal-600 hover:text-teal-700 font-medium">
              foreclosure
            </a>{' '}
            proceedings advance further.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Beyond speed, cash sales offer certainty. Traditional sales fall through all the time — buyers lose their financing, inspections reveal problems, or buyers simply get cold feet. When you accept a cash offer from a reputable buyer, you can count on the sale closing. There&apos;s no nail-biting wait to see if the buyer&apos;s loan will be approved.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Selling As-Is Without Repairs
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Many El Paso homes need repairs that homeowners either can&apos;t afford or don&apos;t want to deal with. Maybe the roof is aging, the HVAC system is on its last legs, or the property has been neglected for years. Traditional buyers typically want move-in ready homes, and their lenders often require properties to meet certain condition standards.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Cash buyers purchase properties in any condition — and we mean any condition. We&apos;ve bought homes with major foundation issues, properties damaged by fire or flooding, houses that haven&apos;t been updated since the 1970s, and even{' '}
            <a href="/elpaso-tx/sell-hoarder-houses" className="text-teal-600 hover:text-teal-700 font-medium">
              hoarded properties
            </a>{' '}
            filled floor to ceiling with belongings. You don&apos;t need to make any repairs, clean anything out, or even mow the lawn. We buy it exactly as it sits.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Avoiding the Hassles of Traditional Sales
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling a home the traditional way is a lot of work. You need to declutter and stage the property, keep it spotlessly clean for showings, accommodate strangers walking through your home at inconvenient times, deal with lowball offers and difficult negotiations, and manage the stress of not knowing when — or if — your home will sell.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            With a cash sale, there are no showings to dozens of potential buyers, no open houses, no staging, and no waiting. You get one offer from one buyer, and if you accept it, you&apos;re done. For homeowners who value their time and peace of mind, this simplicity is worth a lot.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Complex Situations That Complicate Traditional Sales
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Some situations make traditional sales extremely difficult or even impossible. Properties going through{' '}
            <a href="/elpaso-tx/texas-probate-guide" className="text-teal-600 hover:text-teal-700 font-medium">
              probate
            </a>{' '}
            often need to be sold quickly to settle estates. Homes with delinquent property taxes may face{' '}
            <a href="/elpaso-tx/el-paso-tax-foreclosure-information-deadlines" className="text-teal-600 hover:text-teal-700 font-medium">
              tax foreclosure deadlines
            </a>. Properties with code violations, unpermitted additions, or other legal issues can be nearly impossible to sell to traditional buyers.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Experienced cash buyers have dealt with all of these situations and know how to navigate them. We work with probate attorneys, negotiate with tax authorities, and find solutions to title problems that would derail traditional sales. Our experience with complex situations is one of the biggest values we bring to homeowners.
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h4 className="text-xl font-bold text-gray-900 mb-4">
              Common Situations Where Cash Sales Make Sense
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Job relocation with tight timeline</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Inherited property you don&apos;t want</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Divorce requiring quick sale</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Facing foreclosure</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Property needs major repairs</span>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Tired landlord with problem tenants</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Behind on property taxes</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Code violations or liens</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Health issues requiring quick move</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-1"></i>
                  <span className="text-gray-700">Vacant property costing money</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Middle Lead Capture Form */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="text-white">
                <h2 className="text-3xl font-bold mb-4">
                  Ready to See What Your Home Is Worth?
                </h2>
                <p className="text-gray-300 mb-6">
                  Get a no-obligation cash offer for your El Paso property in 24 hours. No repairs, no showings, no hassle.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Fair, honest cash offers</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Close on your timeline</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>We pay all closing costs</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-teal-400 mr-3"></i>
                    <span>Local El Paso company</span>
                  </li>
                </ul>
              </div>
              <div>
                <CashBuyerContactForm
                  formId="middle-cash-buyer-form"
                  title="Get Your Cash Offer"
                  subtitle="No obligation, no pressure"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Understanding Cash Offers Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Understanding Cash Offers: What to Expect
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            One of the most common questions homeowners have is: &quot;How much will a cash buyer pay for my house?&quot; This is a fair question, and understanding how cash buyers determine their offers will help you evaluate whether a cash sale makes financial sense for your situation.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=real%20estate%20investor%20analyzing%20property%20value%20on%20laptop%20with%20calculator%20financial%20documents%20and%20home%20appraisal%20paperwork%20on%20modern%20desk%20showing%20property%20valuation%20process%20professional%20business%20photography%20warm%20lighting&width=800&height=500&seq=cash-buyers-valuation-005&orientation=landscape"
            alt="Property valuation and cash offer calculation"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            How Cash Buyers Calculate Offers
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Cash buyers typically use a formula based on the property&apos;s After Repair Value (ARV) — what the home would be worth in perfect, market-ready condition. From this value, they subtract the estimated cost of repairs, their desired profit margin, and the costs they&apos;ll incur during the renovation and resale process (holding costs, closing costs, etc.).
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            A common formula in the industry is: <strong>Offer = (ARV × 70%) - Repair Costs</strong>. However, this is just a starting point. Actual offers vary based on the specific property, local market conditions, the buyer&apos;s business model, and competition among buyers. Some buyers work on thinner margins than others, and some specialize in properties that other buyers won&apos;t touch.
          </p>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            The Real Cost Comparison
          </h3>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At first glance, cash offers often seem lower than what you might get on the open market. But this comparison isn&apos;t apples to apples. When you sell traditionally, you have significant costs that eat into your proceeds: real estate agent commissions (typically 5-6% of the sale price), closing costs, repair costs to make the home marketable, staging and photography, ongoing mortgage payments while the home sits on the market, and the risk of price reductions if the home doesn&apos;t sell quickly.
          </p>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            When you sell to a cash buyer, you avoid most of these costs. There are no agent commissions, no repair costs, and reputable cash buyers often pay the seller&apos;s closing costs. You also get your money faster, which has real value — especially if you&apos;re paying a mortgage, property taxes, insurance, and utilities on a property you&apos;re trying to sell.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left">Cost Factor</th>
                  <th className="p-4 text-left">Traditional Sale</th>
                  <th className="p-4 text-left">Cash Sale</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                <tr className="border-b">
                  <td className="p-4 font-medium">Agent Commissions</td>
                  <td className="p-4">5-6% of sale price</td>
                  <td className="p-4 text-teal-600 font-semibold">$0</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Repairs/Updates</td>
                  <td className="p-4">$5,000 - $30,000+</td>
                  <td className="p-4 text-teal-600 font-semibold">$0</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Closing Costs</td>
                  <td className="p-4">1-3% of sale price</td>
                  <td className="p-4 text-teal-600 font-semibold">Often $0 (buyer pays)</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Staging/Photography</td>
                  <td className="p-4">$500 - $3,000</td>
                  <td className="p-4 text-teal-600 font-semibold">$0</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Holding Costs (3 months)</td>
                  <td className="p-4">$3,000 - $6,000+</td>
                  <td className="p-4 text-teal-600 font-semibold">Minimal (fast close)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="p-4 font-medium">Time to Close</td>
                  <td className="p-4">45-90+ days</td>
                  <td className="p-4 text-teal-600 font-semibold">7-21 days</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            For a $200,000 home, traditional selling costs can easily reach $20,000-$40,000 or more. When you factor in these costs, the gap between a cash offer and a traditional sale often narrows significantly — and in some cases, you may actually net more from a cash sale, especially if your home needs repairs or would sit on the market for an extended period.
          </p>
        </section>

        {/* How to Choose a Cash Buyer Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            How to Choose a Reputable Cash Home Buyer
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Not all cash buyers are created equal. While most are legitimate businesses, there are some bad actors in the industry who use deceptive practices or fail to follow through on their promises. Here&apos;s how to identify reputable buyers and protect yourself during the process.
          </p>

          <img
            src="https://readdy.ai/api/search-image?query=professional%20handshake%20between%20homeowner%20and%20real%20estate%20investor%20in%20front%20of%20southwestern%20style%20home%20in%20el%20paso%20texas%20showing%20trust%20and%20successful%20business%20deal%20warm%20golden%20hour%20lighting%20professional%20photography&width=800&height=500&seq=cash-buyers-handshake-006&orientation=landscape"
            alt="Trustworthy cash home buyer meeting with homeowner"
            className="w-full h-96 object-cover rounded-lg shadow-lg mb-8"
          />

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Red Flags to Watch For
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                Pressure to Sign Immediately
              </h4>
              <p className="text-gray-700 text-sm">
                Legitimate buyers give you time to consider their offer and consult with family, attorneys, or financial advisors. Anyone pressuring you to sign on the spot is a red flag.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                Offers That Seem Too Good to Be True
              </h4>
              <p className="text-gray-700 text-sm">
                If someone offers you full market value for a property that needs significant repairs, be skeptical. They may plan to renegotiate later or back out of the deal.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                Requests for Upfront Fees
              </h4>
              <p className="text-gray-700 text-sm">
                You should never pay a cash buyer to purchase your home. All legitimate costs are handled at closing through the title company.
              </p>
            </div>
            <div className="bg-red-50 p-5 rounded-lg border-l-4 border-red-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-error-warning-line text-red-500 mr-2"></i>
                Vague or Confusing Contracts
              </h4>
              <p className="text-gray-700 text-sm">
                The purchase agreement should be clear and straightforward. If you don&apos;t understand something, ask for clarification or have an attorney review it.
              </p>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
            Signs of a Reputable Cash Buyer
          </h3>

          <div className="space-y-4 mb-8">
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-check-line text-teal-600 mr-2"></i>
                Local Presence and Track Record
              </h4>
              <p className="text-gray-700 text-sm">
                Look for buyers with a physical presence in El Paso and a history of completed transactions. Check online reviews, ask for references, and verify they&apos;ve actually closed deals in the area.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-check-line text-teal-600 mr-2"></i>
                Transparent Process
              </h4>
              <p className="text-gray-700 text-sm">
                Reputable buyers explain their process clearly, answer your questions honestly, and don&apos;t hide anything. They should be willing to explain how they calculated their offer.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-check-line text-teal-600 mr-2"></i>
                Proof of Funds
              </h4>
              <p className="text-gray-700 text-sm">
                Legitimate cash buyers can provide proof that they have the funds to close. This might be a bank statement, a letter from their bank, or evidence of a line of credit.
              </p>
            </div>
            <div className="bg-teal-50 p-5 rounded-lg border-l-4 border-teal-500">
              <h4 className="font-bold text-gray-900 mb-2">
                <i className="ri-check-line text-teal-600 mr-2"></i>
                Use of Reputable Title Companies
              </h4>
              <p className="text-gray-700 text-sm">
                The transaction should close through a licensed title company that protects both parties. You should be able to choose the title company or at least verify the one they suggest is legitimate.
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            At TREX RE LLC, we encourage homeowners to do their due diligence. Check our reviews, ask us questions, and take the time you need to make your decision. We&apos;re confident in our reputation and our process, and we want you to feel completely comfortable before moving forward. You can also learn more about the{' '}
            <a href="/local-news-el-paso-texas" className="text-teal-600 hover:text-teal-700 font-medium">
              El Paso real estate market
            </a>{' '}
            through our local news and resources section.
          </p>
        </section>

        {/* Cash Buyers vs Other Options Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Cash Buyers vs. Other Selling Options
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            Selling to a cash buyer is just one option available to El Paso homeowners. Understanding how it compares to other methods will help you choose the best approach for your situation.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-lg">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="p-4 text-left">Factor</th>
                  <th className="p-4 text-left">Cash Buyer</th>
                  <th className="p-4 text-left">Real Estate Agent</th>
                  <th className="p-4 text-left">FSBO</th>
                  <th className="p-4 text-left">iBuyer</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm">
                <tr className="border-b">
                  <td className="p-4 font-medium">Timeline</td>
                  <td className="p-4">7-21 days</td>
                  <td className="p-4">45-90+ days</td>
                  <td className="p-4">60-120+ days</td>
                  <td className="p-4">14-45 days</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Commissions/Fees</td>
                  <td className="p-4">None</td>
                  <td className="p-4">5-6%</td>
                  <td className="p-4">0-3%</td>
                  <td className="p-4">5-8%</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Repairs Required</td>
                  <td className="p-4">None</td>
                  <td className="p-4">Usually yes</td>
                  <td className="p-4">Usually yes</td>
                  <td className="p-4">Minor only</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Showings</td>
                  <td className="p-4">One visit</td>
                  <td className="p-4">Many</td>
                  <td className="p-4">Many</td>
                  <td className="p-4">One visit</td>
                </tr>
                <tr className="border-b">
                  <td className="p-4 font-medium">Certainty of Close</td>
                  <td className="p-4">Very high</td>
                  <td className="p-4">Moderate</td>
                  <td className="p-4">Lower</td>
                  <td className="p-4">High</td>
                </tr>
                <tr className="border-b bg-gray-50">
                  <td className="p-4 font-medium">Best For</td>
                  <td className="p-4">Speed, as-is sales</td>
                  <td className="p-4">Maximum price</td>
                  <td className="p-4">Saving commissions</td>
                  <td className="p-4">Newer homes</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Property Condition</td>
                  <td className="p-4">Any condition</td>
                  <td className="p-4">Good to excellent</td>
                  <td className="p-4">Good to excellent</td>
                  <td className="p-4">Good condition</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            If your home is in excellent condition, you have plenty of time, and maximizing your sale price is the top priority, working with a{' '}
            <a href="/elpaso-tx/hire-realtor-el-paso" className="text-teal-600 hover:text-teal-700 font-medium">
              real estate agent
            </a>{' '}
            might be your best option. But if you need to sell quickly, your home needs repairs, or you simply want to avoid the hassles of a traditional sale, a cash buyer offers significant advantages.
          </p>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About Cash Home Buyers
          </h2>

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Are cash home buying companies legitimate?
              </h3>
              <p className="text-gray-700">
                Yes, most cash home buying companies are legitimate businesses. However, like any industry, there are some bad actors. Do your research, check reviews, verify their track record, and never sign anything you don&apos;t understand. Reputable companies like TREX RE LLC are transparent about their process and happy to answer your questions.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How much less than market value do cash buyers pay?
              </h3>
              <p className="text-gray-700">
                Cash offers typically range from 50-85% of market value, depending on the property&apos;s condition and the local market. However, when you factor in the costs you avoid (commissions, repairs, holding costs), the net difference is often much smaller. For properties in poor condition, a cash offer may actually net you more than a traditional sale.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Can I sell my house for cash if I still have a mortgage?
              </h3>
              <p className="text-gray-700">
                Absolutely. Most homes sold to cash buyers have existing mortgages. The mortgage is paid off at closing from the sale proceeds, just like in a traditional sale. As long as the sale price covers your mortgage balance (or you can cover any shortfall), having a mortgage is not a barrier to selling for cash.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Do I need to clean out my house before selling to a cash buyer?
              </h3>
              <p className="text-gray-700">
                No. Cash buyers purchase properties as-is, which includes any belongings left behind. If you want to take certain items, you&apos;re welcome to, but you don&apos;t need to clean out the property. We&apos;ve purchased homes with everything from a few pieces of furniture to entire households of belongings.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                How quickly can I close with a cash buyer?
              </h3>
              <p className="text-gray-700">
                Most cash sales close within 7-21 days, though the exact timeline depends on the title work and your preferences. If you need more time — for example, to find a new place to live — reputable cash buyers will work with your schedule. The flexibility to close on your timeline is one of the benefits of working with a cash buyer.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                What types of properties do cash buyers purchase?
              </h3>
              <p className="text-gray-700">
                Cash buyers purchase virtually any type of residential property: single-family homes, condos, townhouses, duplexes, and small multi-family buildings. They buy properties in any condition, from move-in ready to severely distressed. Some cash buyers also purchase vacant land and commercial properties.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Will I have to pay any fees or commissions?
              </h3>
              <p className="text-gray-700">
                With reputable cash buyers, you pay no fees or commissions. The offer you receive is the amount you&apos;ll get (minus any existing liens or mortgages that need to be paid off). Many cash buyers, including TREX RE LLC, also pay the seller&apos;s closing costs, so you walk away with the full offer amount.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 md:p-12 text-white">
            <div className="text-center mb-8">
              <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-6">
                <i className="ri-home-heart-line text-4xl"></i>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Get Your Cash Offer?
              </h2>
              <p className="text-xl text-teal-100 max-w-2xl mx-auto">
                Whether you&apos;re facing a difficult situation or simply want a fast, hassle-free sale, we&apos;re here to help. Get your no-obligation cash offer today and see if selling for cash is right for you.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8">
              <CashBuyerContactForm
                formId="final-cash-buyer-form"
                title="Get Your Free Cash Offer"
                subtitle="No obligation, no pressure — just a fair offer"
              />
            </div>

            <div className="mt-8 text-center">
              <p className="text-teal-100 mb-4">Prefer to talk? Call us directly:</p>
              <a
                href="tel:+1-915-206-5814"
                className="inline-flex items-center px-8 py-4 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-lg"
              >
                <i className="ri-phone-line mr-2"></i>(915) 206-5814
              </a>
            </div>
          </div>
        </section>

        {/* Related Resources */}
        <section className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="/inherited-homes"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-home-heart-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Selling Inherited Homes</p>
                <p className="text-sm text-gray-600">
                  Guide to selling inherited property for cash
                </p>
              </div>
            </a>
            <a
              href="/avoid-foreclosure"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-shield-check-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Avoid Foreclosure</p>
                <p className="text-sm text-gray-600">
                  How selling for cash can stop foreclosure
                </p>
              </div>
            </a>
            <a
              href="/sell-distressed-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-tools-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Sell Distressed Property</p>
                <p className="text-sm text-gray-600">
                  Options for selling homes that need repairs
                </p>
              </div>
            </a>
            <a
              href="/get-cash-offer-for-my-property"
              className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-money-dollar-circle-line text-teal-600 text-2xl mr-4"></i>
              <div>
                <p className="font-semibold text-gray-900">Get Cash Offer</p>
                <p className="text-sm text-gray-600">
                  Request your no-obligation cash offer now
                </p>
              </div>
            </a>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
}
